/**
 * Customer Assets Component
 * Displays customer's audiences and simulations
 */

import React, { useState } from 'react';
import { Users, Play, ChevronDown, ChevronUp, Layers, UserCircle } from 'lucide-react';
import { CustomerAudience, CustomerSimulation, USECASE_LABELS } from '../types';

interface CustomerAssetsProps {
  audiences: CustomerAudience[];
  simulations: CustomerSimulation[];
}

const BRAND_PRIMARY = '#03e8d3';

export const CustomerAssets: React.FC<CustomerAssetsProps> = ({ audiences, simulations }) => {
  const [audiencesExpanded, setAudiencesExpanded] = useState(true);
  const [simulationsExpanded, setSimulationsExpanded] = useState(true);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatUseCase = (useCase: string | null): string => {
    if (!useCase) return 'N/A';
    return USECASE_LABELS[useCase] || useCase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Audiences */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <button
          onClick={() => setAudiencesExpanded(!audiencesExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Audiences</h3>
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {audiences.length}
            </span>
          </div>
          {audiencesExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {audiencesExpanded && (
          <div className="px-6 pb-6">
            {audiences.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No audiences created
              </div>
            ) : (
              <div className="space-y-3">
                {audiences.map((audience) => (
                  <div
                    key={audience.id}
                    className="p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{audience.name}</h4>
                        {audience.industry && (
                          <p className="text-sm text-gray-500 mt-0.5">{audience.industry}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatDate(audience.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Layers className="w-4 h-4" style={{ color: BRAND_PRIMARY }} />
                        <span>{audience.segment_count} segments</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <UserCircle className="w-4 h-4" style={{ color: BRAND_PRIMARY }} />
                        <span>{audience.persona_count} personas</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Simulations */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <button
          onClick={() => setSimulationsExpanded(!simulationsExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Play className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Simulations</h3>
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {simulations.length}
            </span>
          </div>
          {simulationsExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {simulationsExpanded && (
          <div className="px-6 pb-6">
            {simulations.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No simulations run in this time period
              </div>
            ) : (
              <div className="space-y-3">
                {simulations.map((simulation) => (
                  <div
                    key={simulation.id}
                    className="p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{simulation.name}</h4>
                        <span className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {formatUseCase(simulation.use_case)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatDate(simulation.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerAssets;
